/**
 * Shared email emulation types.
 *
 * @module
 */

import { z } from 'zod';
import type { PluginContext } from '../../plugin.js';

export type EmailProvider = 'ses' | 'sendgrid' | 'resend' | 'postmark' | 'mailgun';

export interface EmailEmuConfig {
  /** AWS SES regions whose API hosts to intercept (default ['us-east-1']). */
  sesRegions?: string[];
  /** Extra custom hosts to intercept (e.g. a self-hosted provider API). */
  extraDomains?: string[];
}

/** Normalized capture of one outbound email, provider-agnostic. */
export interface CapturedEmail {
  /** Stable per-send id, used as the inbox dedup key (messageId). */
  id: string;
  provider: EmailProvider;
  from: string;
  fromName?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string | null;
  text: string | null;
  html: string | null;
  /** Selected provider headers, with sensitive values redacted. */
  headers: Record<string, string>;
  attachments: Array<{ filename?: string; contentType?: string; size?: number }>;
  /** ISO-8601 capture time. */
  receivedAt: string;
}

/** Fixture is intentionally empty; there is no seed data, only captured state. */
export const emailFixtureSchema = z.object({}).passthrough();
export type EmailFixture = z.infer<typeof emailFixtureSchema>;

export interface EmailState {
  /** Every email sent since boot or last reset, used as the assertion surface. */
  sentEmails: CapturedEmail[];
}

export type EmailCtx = PluginContext<EmailFixture, EmailState>;
